import api from "@core/api/api";

export const returnVoucherPDFService = {
  /**
   * Generate PDF for a return voucher
   */
  generatePDF: async (returnVoucherId) => {
    try {
      const response = await api.post(
        `/return-vouchers/${returnVoucherId}/generate-pdf`
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to generate PDF"
      );
    }
  },

  /**
   * Download PDF for a return voucher
   */
  downloadPDF: async (returnVoucherId, filename = "bon-retour.pdf") => {
    try {
      const response = await api.get(
        `/return-vouchers/${returnVoucherId}/download-pdf`,
        {
          responseType: "blob",
        }
      );

      // Create blob URL from the response data (already a Blob)
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to download PDF"
      );
    }
  },

  /**
   * Generate and download PDF in one operation
   */
  generateAndDownload: async (returnVoucherId, filename = "bon-retour.pdf") => {
    try {
      // Single API call that generates and returns the PDF blob
      const response = await api.post(
        `/return-vouchers/${returnVoucherId}/pdf`,
        {},
        {
          responseType: "blob",
        }
      );

      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to generate and download PDF"
      );
    }
  },

  /**
   * Get return voucher data (used for checking if PDF exists)
   */
  getReturnVoucher: async (returnVoucherId) => {
    try {
      const response = await api.get(`/return-vouchers/${returnVoucherId}`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch return voucher"
      );
    }
  },

  /**
   * Bulk generate PDFs for multiple return vouchers
   */
  bulkGeneratePDF: async (returnVoucherIds) => {
    const results = {
      success: [],
      failed: [],
    };

    for (const id of returnVoucherIds) {
      try {
        await returnVoucherPDFService.generatePDF(id);
        results.success.push(id);
      } catch (error) {
        results.failed.push({ id, error: error.message });
      }
    }

    return results;
  },

  /**
   * Bulk download PDFs for multiple return vouchers
   */
  bulkDownloadPDF: async (returnVoucherIds) => {
    const results = {
      success: [],
      failed: [],
    };

    for (const id of returnVoucherIds) {
      try {
        await returnVoucherPDFService.downloadPDF(id, `bon-retour-${id}.pdf`);
        results.success.push(id);
      } catch (error) {
        results.failed.push({ id, error: error.message });
      }
    }

    return results;
  },
};
